import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined';
import Hls from 'hls.js';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { useRecordings } from '../../hooks/useRecordings';
import { getRecordingUrl, getSnapshotUrl, getDownloadUrl, updateEventById, bulkUpdateEventsById } from 'api/canvms';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { useCANvisionContext } from '../../context/useCANvisionContext';
import { SeverityChip } from 'components/shared/SeverityChip';
import RecordingBrowser from '../RecordingBrowser/RecordingBrowser';
import Timeline from '../Timeline/Timeline';
import PlaybackControls from '../PlaybackControls/PlaybackControls';
import PTZControls from '../PTZControls/PTZControls';
import PromoteEventDialog from '../PromoteEventDialog';

/**
 * Format a Date to a relative "time ago" string.
 * Returns values like "5s ago", "2m ago", "1h ago".
 */
function formatTimeAgo(date) {
  if (!date) return '';
  const now = Date.now();
  const then = date instanceof Date ? date.getTime() : new Date(date).getTime();
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function CameraDetail({ open, onClose, camera, siteId, siteName = '', liveStreamUrl, snapshotUrl, streamType = 'hls', events: eventsProp, onEventsEmpty, onEventRemoved }) {
  const [mode, setMode] = useState('live'); // 'live' or 'playback'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const imgRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const [snapshotSrc, setSnapshotSrc] = useState(null);

  // -- Event list state -------------------------------------------------------
  const [localEvents, setLocalEvents] = useState([]);
  const [loadingEventIds, setLoadingEventIds] = useState(new Set());
  const [dismissAllLoading, setDismissAllLoading] = useState(false);
  const { notifySuccess, notifyError } = useNotificationProvider();
  const { lastEventUpdate, claimedEvents, claimEvent, releaseEvent } = useCANvisionContext();
  const { userInfo } = useSelector(selectUserInfo);
  const operatorName = userInfo?.fullname || [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ') || 'Unknown';
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteEvent, setPromoteEvent] = useState(null);
  const hasEvents = Array.isArray(eventsProp) && eventsProp.length > 0;

  // Initialize local events from props when modal opens or events change
  useEffect(() => {
    if (open && eventsProp) {
      setLocalEvents(eventsProp);
    }
  }, [open, eventsProp]);

  // Reset event state on close
  useEffect(() => {
    if (!open) {
      setLocalEvents([]);
      setLoadingEventIds(new Set());
      setDismissAllLoading(false);
    }
  }, [open]);

  // -- Claim/Release (hard lock) ---------------------------------------------

  // Keep a ref of localEvents so the cleanup function always has current data
  const localEventsRef = useRef(localEvents);
  useEffect(() => {
    localEventsRef.current = localEvents;
  }, [localEvents]);

  // Claim all events when modal opens; release on close / unmount
  useEffect(() => {
    if (!open || !hasEvents) return;

    // Claim each event — failures are caught silently (modal still opens)
    const eventIds = (eventsProp || []).map((e) => e.eventId).filter(Boolean);
    eventIds.forEach((id) => {
      try {
        claimEvent(id, operatorName);
      } catch (err) {
        console.error('[CameraDetail] Failed to claim event', id, err);
      }
    });

    // Release all events on close or unmount
    return () => {
      // Use ref so we release the current set of events, not the stale closure
      const currentIds = localEventsRef.current.map((e) => e.eventId).filter(Boolean);
      // Also include the original event IDs in case localEvents was already cleared
      const allIds = [...new Set([...currentIds, ...eventIds])];
      allIds.forEach((id) => {
        try {
          releaseEvent(id);
        } catch (err) {
          console.error('[CameraDetail] Failed to release event', id, err);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasEvents]);

  // Derive whether any local event is claimed by ANOTHER operator
  const claimedByOtherInfo = useMemo(() => {
    if (!claimedEvents || localEvents.length === 0) return null;
    for (const evt of localEvents) {
      const claim = claimedEvents.get(evt.eventId);
      if (claim && claim.operatorName !== operatorName) {
        return claim; // { operatorName, claimedAt }
      }
    }
    return null;
  }, [claimedEvents, localEvents, operatorName]);

  const isLockedByOther = !!claimedByOtherInfo;

  // Listen to context lastEventUpdate to catch remote status changes
  useEffect(() => {
    if (!lastEventUpdate || !open) return;

    const removedId = lastEventUpdate.id;
    const newStatus = lastEventUpdate.status;

    // Only remove if the event transitioned away from 'new'
    if (newStatus === 'new') return;

    setLocalEvents((prev) => {
      const idx = prev.findIndex((e) => e.eventId === removedId);
      if (idx === -1) return prev; // not in our list
      const next = prev.filter((e) => e.eventId !== removedId);
      // If list is now empty, auto-close
      if (next.length === 0 && onEventsEmpty) {
        // Defer the close to avoid state update during render
        setTimeout(() => onEventsEmpty(), 0);
      }
      return next;
    });
  }, [lastEventUpdate, open, onEventsEmpty]);

  const { recordings, loading: recordingsLoading, error: recordingsError } = useRecordings(
    siteId,
    camera?.id,
    mode === 'playback' ? selectedDate : null
  );

  // Setup HLS player
  const setupHls = useCallback((url, isLive = false) => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Cleanup previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: isLive,
        backBufferLength: isLive ? 30 : 90,
      });

      hls.loadSource(url);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
        setIsPlaying(true);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('[CANVMS/CameraDetail] HLS fatal error:', data);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          }
        }
      });

      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      video.play().catch(() => {});
      setIsPlaying(true);
    }
  }, []);

  // Track if we've already set up the live stream to avoid re-initialization
  const liveStreamInitialized = useRef(false);
  const lastLiveStreamUrl = useRef(null);

  // Handle mode change - only setup HLS once per URL, not on every render
  useEffect(() => {
    // Clear any existing refresh timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (mode === 'live' && liveStreamUrl) {
      // Handle JPEG snapshot refresh (for JPEG-only cameras)
      if (streamType === 'jpeg') {
        const loadSnapshot = () => {
          const url = `${liveStreamUrl}${liveStreamUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
          setSnapshotSrc(url);
        };
        loadSnapshot();
        refreshTimerRef.current = setInterval(loadSnapshot, 1000);
        liveStreamInitialized.current = true;
        lastLiveStreamUrl.current = liveStreamUrl;
      } else {
        // HLS or MJPEG stream
        // Only re-initialize if URL actually changed or hasn't been initialized
        if (!liveStreamInitialized.current || lastLiveStreamUrl.current !== liveStreamUrl) {
          setupHls(liveStreamUrl, true);
          liveStreamInitialized.current = true;
          lastLiveStreamUrl.current = liveStreamUrl;
        }
      }
      setSelectedRecording(null);
      setPlaybackUrl(null);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [mode, liveStreamUrl, streamType, setupHls]);

  // Handle recording selection
  const handleRecordingSelect = async (recording) => {
    setSelectedRecording(recording);
    try {
      const response = await getRecordingUrl(siteId, camera.id, recording.filename);
      const url = response.data?.playback_url;
      setPlaybackUrl(url);
      setupHls(url, false);
    } catch (err) {
      console.error('[CANVMS/CameraDetail] Error getting recording URL:', err);
    }
  };

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, []);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      setMode('live');
      setSelectedRecording(null);
      setPlaybackUrl(null);
      setSnapshotSrc(null);
      // Reset initialization tracking so next open will setup fresh
      liveStreamInitialized.current = false;
      lastLiveStreamUrl.current = null;
    }
  }, [open]);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const handleSeek = (time) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = time;
    }
  };

  const handleSpeedChange = (speed) => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = speed;
      setPlaybackSpeed(speed);
    }
  };

  const handleSnapshot = async () => {
    try {
      const response = await getSnapshotUrl(siteId, camera.id);
      const url = response.data?.snapshot_url;
      if (url) {
        // Open snapshot in new tab or trigger download
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('[CANVMS/CameraDetail] Error getting snapshot:', err);
    }
  };

  const handleDownload = async () => {
    if (!selectedRecording) return;

    try {
      const response = await getDownloadUrl(siteId, camera.id, selectedRecording.filename);
      const url = response.data?.download_url;
      if (url) {
        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = selectedRecording.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('[CANVMS/CameraDetail] Error downloading recording:', err);
    }
  };

  // -- Event action handlers ---------------------------------------------------

  /** Remove an event from local state and trigger auto-close if empty. */
  const removeEventLocally = useCallback((eventId) => {
    setLocalEvents((prev) => {
      const next = prev.filter((e) => e.eventId !== eventId);
      if (next.length === 0 && onEventsEmpty) {
        setTimeout(() => onEventsEmpty(), 0);
      }
      return next;
    });
  }, [onEventsEmpty]);

  /** Mark loading state for a specific event button action. */
  const setEventLoading = useCallback((eventId, isLoading) => {
    setLoadingEventIds((prev) => {
      const next = new Set(prev);
      if (isLoading) {
        next.add(eventId);
      } else {
        next.delete(eventId);
      }
      return next;
    });
  }, []);

  const handleAcknowledge = useCallback(async (eventId) => {
    setEventLoading(eventId, true);
    try {
      await updateEventById(eventId, 'acknowledged');
      notifySuccess('Event acknowledged');
      removeEventLocally(eventId);
      onEventRemoved?.(eventId);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to acknowledge event';
      console.error('[CANVMS/CameraDetail] Error acknowledging event:', err);
      notifyError(message);
    } finally {
      setEventLoading(eventId, false);
    }
  }, [notifySuccess, notifyError, removeEventLocally, setEventLoading, onEventRemoved]);

  const handleDismissEvent = useCallback(async (eventId) => {
    setEventLoading(eventId, true);
    try {
      await updateEventById(eventId, 'dismissed');
      notifySuccess('Event dismissed');
      removeEventLocally(eventId);
      onEventRemoved?.(eventId);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to dismiss event';
      console.error('[CANVMS/CameraDetail] Error dismissing event:', err);
      notifyError(message);
    } finally {
      setEventLoading(eventId, false);
    }
  }, [notifySuccess, notifyError, removeEventLocally, setEventLoading, onEventRemoved]);

  const handleDismissAll = useCallback(async () => {
    if (localEvents.length === 0) return;
    setDismissAllLoading(true);
    try {
      const eventIds = localEvents.map((e) => e.eventId);
      await bulkUpdateEventsById(eventIds, 'dismissed');
      notifySuccess(`${eventIds.length} event${eventIds.length > 1 ? 's' : ''} dismissed`);
      // Notify attention feed for each dismissed event
      eventIds.forEach((id) => onEventRemoved?.(id));
      setLocalEvents([]);
      if (onEventsEmpty) {
        setTimeout(() => onEventsEmpty(), 0);
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to dismiss all events';
      console.error('[CANVMS/CameraDetail] Error dismissing all events:', err);
      notifyError(message);
    } finally {
      setDismissAllLoading(false);
    }
  }, [localEvents, notifySuccess, notifyError, onEventsEmpty, onEventRemoved]);

  // -- Promote to Incident handlers -------------------------------------------

  const handlePromoteClick = useCallback((evt) => {
    setPromoteEvent(evt);
    setPromoteDialogOpen(true);
  }, []);

  const handlePromoteClose = useCallback((result) => {
    setPromoteDialogOpen(false);
    setPromoteEvent(null);
    if (result?.promoted && result?.eventId) {
      removeEventLocally(result.eventId);
      onEventRemoved?.(result.eventId);
    }
  }, [removeEventLocally, onEventRemoved]);

  const cameraName = camera?.display_name || camera?.name || camera?.stream_id || 'Camera';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh' },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            {cameraName}
          </Typography>

          <Tabs value={mode} onChange={(e, v) => setMode(v)} sx={{ mx: 2 }}>
            <Tab icon={<LiveTvIcon />} iconPosition="start" label="Live" value="live" />
            {/* Playback tab hidden until a client requires continuous recording */}
            {/* <Tab icon={<VideoLibraryIcon />} iconPosition="start" label="Playback" value="playback" /> */}
          </Tabs>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left column: Video + Events */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Video player area */}
            <Box sx={{
              flex: hasEvents && localEvents.length > 0 ? '0 0 auto' : 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'black',
              ...(hasEvents && localEvents.length > 0 && { maxHeight: '55%' }),
            }}>
              {/* Video/Image element */}
              <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
                {/* Use img for JPEG snapshots in live mode */}
                {mode === 'live' && streamType === 'jpeg' ? (
                  <img
                    ref={imgRef}
                    src={snapshotSrc || snapshotUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                    alt="Camera feed"
                  />
                ) : (
                  <video
                    ref={videoRef}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                    poster={snapshotUrl}
                    muted={mode === 'live'}
                    playsInline
                  />
                )}
              </Box>

              {/* Timeline (only in playback mode) */}
              {mode === 'playback' && (
                <Paper sx={{ mx: 1, mb: 1, p: 1 }}>
                  <Timeline
                    recordings={recordings}
                    selectedDate={selectedDate}
                    currentTime={currentTime}
                    currentRecording={selectedRecording}
                    onRecordingClick={handleRecordingSelect}
                  />
                </Paper>
              )}

              {/* Playback controls (only in playback mode with recording selected) */}
              {mode === 'playback' && (
                <Box sx={{ mx: 1, mb: 1 }}>
                  <PlaybackControls
                    isPlaying={isPlaying}
                    currentTime={currentTime}
                    duration={duration}
                    playbackSpeed={playbackSpeed}
                    onPlayPause={handlePlayPause}
                    onSeek={handleSeek}
                    onSpeedChange={handleSpeedChange}
                    onSnapshot={handleSnapshot}
                    onDownload={handleDownload}
                    disabled={!selectedRecording}
                  />
                </Box>
              )}

              {/* PTZ Controls (only in live mode for PTZ capable cameras) */}
              {mode === 'live' && camera?.ptz_capable && (
                <Box sx={{ mx: 1, mb: 1 }}>
                  <PTZControls
                    siteId={siteId}
                    cameraId={camera.id}
                  />
                </Box>
              )}
            </Box>

            {/* Active Events section — only shown when events are provided */}
            {hasEvents && localEvents.length > 0 && (
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default',
              }}>
                {/* Hard lock banner — shown when another operator has claimed any event */}
                {isLockedByOther && (
                  <Alert severity="info" sx={{ borderRadius: 0 }}>
                    Being reviewed by {claimedByOtherInfo.operatorName}
                  </Alert>
                )}

                {/* Events header */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  borderTop: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Active Events ({localEvents.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={dismissAllLoading ? <CircularProgress size={14} /> : <VisibilityOffIcon />}
                    disabled={dismissAllLoading || localEvents.length === 0 || isLockedByOther}
                    onClick={handleDismissAll}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Dismiss All
                  </Button>
                </Box>

                {/* Scrollable event list */}
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  px: 2,
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}>
                  {localEvents.map((evt) => {
                    const isLoading = loadingEventIds.has(evt.eventId);
                    return (
                      <Paper
                        key={evt.eventId}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        {/* Row 1: event type, severity, time */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {evt.eventType?.replace(/_/g, ' ') || 'Unknown'}
                          </Typography>
                          <SeverityChip severity={evt.severity} size="small" />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {formatTimeAgo(evt.addedAt)}
                          </Typography>
                        </Box>

                        {/* Row 2: detection count + summary */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {evt.detectionCount > 0 && (
                            <Chip
                              label={`${evt.detectionCount} detection${evt.detectionCount !== 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          )}
                          {evt.summary && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                              {evt.summary}
                            </Typography>
                          )}
                        </Box>

                        {/* Row 3: action buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={isLoading ? <CircularProgress size={14} /> : <CheckCircleOutlineIcon />}
                            disabled={isLoading || dismissAllLoading || isLockedByOther}
                            onClick={() => handleAcknowledge(evt.eventId)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Acknowledge
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={isLoading ? <CircularProgress size={14} /> : <VisibilityOffIcon />}
                            disabled={isLoading || dismissAllLoading || isLockedByOther}
                            onClick={() => handleDismissEvent(evt.eventId)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Dismiss
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={isLoading ? <CircularProgress size={14} /> : <LocalPoliceOutlinedIcon />}
                            disabled={isLoading || dismissAllLoading || isLockedByOther}
                            onClick={() => handlePromoteClick(evt)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Promote to Incident
                          </Button>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* Recording browser sidebar (only in playback mode) */}
          {mode === 'playback' && (
            <Paper
              sx={{
                width: 300,
                borderLeft: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <RecordingBrowser
                recordings={recordings}
                selectedRecording={selectedRecording}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onRecordingSelect={handleRecordingSelect}
                loading={recordingsLoading}
                error={recordingsError}
                siteId={siteId}
                cameraId={camera?.id}
              />
            </Paper>
          )}
        </Box>
      </DialogContent>

      {/* Promote to Incident dialog */}
      <PromoteEventDialog
        open={promoteDialogOpen}
        onClose={handlePromoteClose}
        event={promoteEvent}
        cameraName={cameraName}
        siteName={siteName}
        siteId={siteId}
        cameraId={camera?.id}
      />
    </Dialog>
  );
}

export default CameraDetail;
